import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { progressReviewApi } from '../api/progress-reviews';
import { apprenticeApi } from '../api/apprentices';
import { ProgressReview } from '../types/progress-review.types';
import { Apprentice } from '../types';

export const ProgressReviewsList: React.FC = () => {
  const { user, userRole } = useAuth();
  const [reviews, setReviews] = useState<ProgressReview[]>([]);
  const [apprentices, setApprentices] = useState<Apprentice[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApprenticeId, setSelectedApprenticeId] = useState<string>('');

  const loadData = useCallback(async () => {
    if (!user?.tenant_id) return;

    try {
      setLoading(true);

      if (userRole === 'learner') {
        // Learners see only their own reviews
        const apprenticesRes = await apprenticeApi.getAll(user.tenant_id);
        const myApprentice = apprenticesRes.data.find(
          (a: Apprentice) => a.canvas_user_id === user.user_id || a.email === user.email
        );

        if (myApprentice) {
          setApprentices([myApprentice]);
          const reviewsRes = await progressReviewApi.getAll(
            user.tenant_id,
            myApprentice.id
          );
          setReviews(reviewsRes.data);
        }
      } else {
        // Load apprentices based on role
        const instructorEmail = userRole === 'instructor' ? user.email : undefined;
        const apprenticesRes = await apprenticeApi.getAll(
          user.tenant_id,
          undefined,
          instructorEmail
        );
        setApprentices(apprenticesRes.data);

        // Load reviews
        const reviewsRes = await progressReviewApi.getAll(
          user.tenant_id,
          selectedApprenticeId || undefined,
          instructorEmail
        );
        
        // Filter reviews to only show those for apprentices in caseload
        const apprenticeIds = new Set(apprenticesRes.data.map((a: Apprentice) => a.id));
        const filteredReviews = reviewsRes.data.filter((r: ProgressReview) =>
          apprenticeIds.has(r.apprentice_id)
        );
        
        setReviews(filteredReviews);
      }
    } catch (err) {
      console.error('Failed to load progress reviews:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.tenant_id, user?.user_id, user?.email, userRole, selectedApprenticeId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getApprenticeName = (apprenticeId: string) => {
    const apprentice = apprentices.find(a => a.id === apprenticeId);
    return apprentice ? `${apprentice.first_name} ${apprentice.last_name}` : 'Unknown';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const getReviewTypeColor = (type: string) => {
    const colors: any = {
      'Initial': 'bg-blue-100 text-blue-800',
      'Mid-point': 'bg-yellow-100 text-yellow-800',
      'Quarterly': 'bg-purple-100 text-purple-800',
      'Final': 'bg-green-100 text-green-800',
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-600">Loading progress reviews...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {userRole === 'learner' ? 'My Progress Reviews' : userRole === 'instructor' ? 'My Progress Reviews' : 'Progress Reviews'}
          </h1>
          <p className="text-gray-600">
            {userRole === 'learner' 
              ? 'View your development reviews and progress'
              : 'Track and manage apprentice development reviews'}
          </p>
        </div>
        {(userRole === 'instructor' || userRole === 'administrator') && (
          <Link
            to="/progress-reviews/new"
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
          >
            Create Review
          </Link>
        )}
      </div>

      {/* Filter by Apprentice - Not shown for learners */}
      {userRole !== 'learner' && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Filter by Apprentice
          </label>
          <select
            value={selectedApprenticeId}
            onChange={(e) => setSelectedApprenticeId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Apprentices</option>
            {apprentices.map(apprentice => (
              <option key={apprentice.id} value={apprentice.id}>
                {apprentice.first_name} {apprentice.last_name}
              </option>
            ))}
          </select>
        </div>
      )}

      {reviews.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-600 mb-4">
            {selectedApprenticeId
              ? 'No progress reviews found for this apprentice.'
              : userRole === 'learner'
              ? 'No progress reviews have been created for you yet.'
              : 'No progress reviews created yet.'}
          </p>
          {(userRole === 'instructor' || userRole === 'administrator') && (
            <Link
              to="/progress-reviews/new"
              className="inline-block px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Create First Review
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    {userRole !== 'learner' && (
                      <h3 className="text-lg font-semibold text-gray-900">
                        {getApprenticeName(review.apprentice_id)}
                      </h3>
                    )}
                    <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getReviewTypeColor(review.review_type)}`}>
                      {review.review_type}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mb-3">
                    Review Date: {formatDate(review.review_date)}
                  </div>
                  {review.progress_summary && (
                    <p className="text-gray-700 text-sm line-clamp-2 mb-3">
                      {review.progress_summary}
                    </p>
                  )}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Employer:</span>
                      {review.employer_signature ? (
                        <span className="text-green-600 font-medium">✓ Signed</span>
                      ) : (
                        <span className="text-orange-600">Pending</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-500">Apprentice:</span>
                      {review.apprentice_signature ? (
                        <span className="text-green-600 font-medium">✓ Signed</span>
                      ) : (
                        <span className="text-orange-600">Pending</span>
                      )}
                    </div>
                  </div>
                </div>
                <Link
                  to={`/progress-reviews/${review.id}`}
                  className="ml-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition"
                >
                  View Details
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 text-sm text-gray-600">
        Showing {reviews.length} review{reviews.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
};
